import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { Button, Divider, Modal, Tooltip, Upload } from 'antd';
import AudioAsset from '@assets/images/asset-placeholder/audio.svg?url';
import FileAsset from '@assets/images/asset-placeholder/document.svg?url';
import VideoAsset from '@assets/images/asset-placeholder/video.svg?url';
import { useTypedSelector } from '@hooks';
import { toggleSelectFilesModal } from '@redux/actions/modalAction';
import { useDebounced } from '@helpers/useDebounced';
import LottieComponent from '@components/Lottie';
import { getFiles } from '@helpers/getFiles';
import { SelectFilesModalState } from '@redux/types/modalType';
import { CrossIcon, RemoveFileActionIcon } from '@icons';
import './SelectFilesModal.less';

function FilePreviewField({ file }: { file: File }) {
  const fallbackUrl = useMemo(() => {
    if (file?.type?.includes('audio')) return AudioAsset;
    if (file?.type?.includes('video')) return VideoAsset;
    return FileAsset;
  }, [file]);

  const previewUrl = useMemo(() => {
    return file?.type?.includes('image') ? URL.createObjectURL(file) : null;
  }, [file]);

  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  return (
    <picture>
      {!!previewUrl && <source srcSet={previewUrl} type={file.type} />}
      <img src={fallbackUrl} alt="" />
    </picture>
  );
}

function SelectFilesModalInner({
  visible,
  isMultiple = true,
  onFinish
}: SelectFilesModalState) {
  const dispatch = useDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const [showCanceledModal, setShowCanceledModal] = useState<boolean>(false);

  const onAddFile = async (data: File | DataTransferItemList) => {
    const files = await getFiles(data);
    setFiles((prevFiles) => [...prevFiles, ...(files || [])]);
  };

  const onRemoveFile = (fileName: string) => {
    setFiles((prevFiles) => prevFiles.filter((item) => item.name !== fileName));
  };

  const onCancel = () => {
    if (files?.length) {
      setShowCanceledModal(true);
      return;
    }
    dispatch(toggleSelectFilesModal(null));
  };

  const onAddFiles = () => {
    onFinish(files);
    dispatch(toggleSelectFilesModal(null));
  };

  return (
    <>
      <Modal
        open={visible}
        footer={null}
        centered={true}
        closeIcon={<CrossIcon />}
        destroyOnClose
        width={688}
        onCancel={onCancel}
        className="select-files-modal"
        wrapProps={{
          onDragOver: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'none';
            if (e.dataTransfer.types.includes('Files')) {
              const target = e.target as HTMLElement;
              if (!target.classList.contains('ant-modal-wrap')) {
                e.dataTransfer.dropEffect = 'copy';
              }
            }
          },
          onDragEnter: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
          },
          onDragLeave: (e: React.DragEvent) => {
            e.stopPropagation();
          },
          onDropCapture: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const target = e.target as HTMLElement;
            if (!target.classList.contains('ant-modal-wrap')) {
              e.dataTransfer.dropEffect = 'copy';
              onAddFile(e.dataTransfer.items);
            }
          }
        }}
      >
        <div className="modal_container">
          <h2 className="modal_container__title">Upload attachments</h2>
          <p className="modal_container__subtitle">
            Upload videos, images, PDFs, audio files and{' '}
            <span>other formats</span> .
          </p>
          {!!files?.length && <Divider style={{ margin: '32px 0' }} />}
          <Upload
            multiple={isMultiple}
            showUploadList={false}
            beforeUpload={(file) => {
              onAddFile(file);
              return false;
            }}
            openFileDialogOnClick={false}
          >
            {!!files?.length && (
              <div className={classNames('file_list_container')}>
                {files.map((el: File) => {
                  const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
                  const name = el.name.replace(/\.[^/.]+$/, '');
                  const extension = el.name.split('.').pop();
                  let fs = 'n/a';
                  if (el.size > 0) {
                    const i: number = parseInt(
                      Math.floor(Math.log(el.size) / Math.log(1024)).toString(),
                      10
                    );
                    if (i === 0) fs = `${el.size} ${sizes[i]}`;
                    fs = `${(el.size / 1024 ** i).toFixed(1)} ${sizes[i]}`;
                  }
                  return (
                    <div key={el.name} className="file_item">
                      <div className="file_general">
                        <FilePreviewField file={el} />
                        <div className="file_info">
                          <Tooltip
                            placement="top"
                            title={`${name}.${extension}`}
                            overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                          >
                            {name}.{extension}
                          </Tooltip>
                        </div>
                      </div>
                      <div className="file_size">{fs}</div>
                      <div
                        className="file_remove"
                        onClick={() => onRemoveFile(el.name)}
                      >
                        <RemoveFileActionIcon />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Upload>
          {!files?.length && (
            <Upload
              multiple={isMultiple}
              showUploadList={false}
              beforeUpload={(file) => {
                onAddFile(file);
                return false;
              }}
              className="select-files-modal_upload-area"
            >
              <div className="select-files-modal_upload-block">
                <LottieComponent className="icon" view="upload" />
                <p>Drag and drop the attachments here</p>
                <div className="divider">or</div>
                <Button
                  style={{ width: 152, height: 48, padding: '8px 15px' }}
                  type="primary"
                >
                  Browse files
                </Button>
              </div>
            </Upload>
          )}
          {!!files?.length && (
            <div className="modal_container__actions">
              <Button
                type="text"
                size="large"
                style={{
                  marginRight: '16px',
                  border: 'none',
                  height: 48,
                  padding: '8px 16px'
                }}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={onAddFiles}
                style={{ width: 152, height: 48, padding: '8px 15px' }}
              >
                Add media
              </Button>
            </div>
          )}
        </div>
      </Modal>
      <Modal
        open={showCanceledModal}
        footer={null}
        centered={true}
        destroyOnClose
        closeIcon={<CrossIcon />}
        width={468}
        onCancel={() => setShowCanceledModal(false)}
      >
        <div className="modal_container">
          <h2
            className="modal_container__title"
            style={{ marginBottom: '12px' }}
          >
            Cancel attachment
          </h2>
          <p className="modal_container__subtitle">
            Are you sure you want to cancel this upload attachment?
          </p>
          <div className="modal_container__actions">
            <Button
              type="text"
              size="large"
              style={{
                marginRight: '16px',
                border: 'none',
                height: 48,
                padding: '8px 32px'
              }}
              onClick={() => setShowCanceledModal(false)}
            >
              No, don&apos;t cancel
            </Button>
            <Button
              type="primary"
              size="large"
              style={{ width: 152, height: 48, padding: '8px 15px' }}
              onClick={() => dispatch(toggleSelectFilesModal(null))}
            >
              Yes, cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default function SelectFilesModal() {
  const modalState = useTypedSelector(({ modal }) => modal.selectFilesModal);
  const visibleDebounced = useDebounced(modalState?.visible, 300, false);
  const visible = modalState?.visible || visibleDebounced;
  if (!modalState || !visible) return null;
  return <SelectFilesModalInner {...modalState} key={modalState.key} />;
}
